/** Sesión y fetch del portal Instituto (namespace separado del staff/ciudadano). */
import { formatApiError } from './apiErrors';

const TOKEN_KEY = 'institute_token';
const USER_KEY = 'institute_user';

function resolveApiBase() {
  if (process.env.NODE_ENV === 'development') return '/api';
  const configured = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  if (!configured) return '/api';
  if (typeof window !== 'undefined') {
    const { hostname, port } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocal && configured.includes('localhost:8001') && port && port !== '8001') {
      return '/api';
    }
  }
  return `${configured}/api`;
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setSession(token, user) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (user != null) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/** Capabilities del usuario de sesión Instituto (Set o null si no hay lista). */
export function getInstituteCapabilities() {
  const user = getUser();
  if (!user) return null;
  if (Array.isArray(user.capabilities)) return new Set(user.capabilities);
  if (Array.isArray(user.perms)) return new Set(user.perms);
  return null;
}

export function hasInstituteCapability(capability) {
  if (!capability) return false;
  const caps = getInstituteCapabilities();
  if (!caps) return true; // sin lista en sesión: no bloquear UI (backend decide)
  return caps.has(capability);
}

export function hasAnyInstituteCapability(...capabilities) {
  const list = capabilities.filter(Boolean);
  if (!list.length) return false;
  const caps = getInstituteCapabilities();
  if (!caps) return true;
  return list.some((c) => caps.has(c));
}

export function isInstituteSession() {
  const token = getToken();
  const user = getUser();
  if (!token || !user) return false;
  const portal = user.portal || user.aud || user.token_type || user.audience;
  if (portal && String(portal).toLowerCase().includes('institute')) return true;
  // Si el backend marca portal explícitamente
  if (user.portal === 'institute') return true;
  // Token presente + rol institute_*
  if (typeof user.role === 'string' && user.role.startsWith('institute_')) return true;
  if (typeof user.role === 'string' && (
    user.role === 'notaries_director'
    || user.role === 'citizen_services_subcounsel'
    || user.role.startsWith('notarial_')
    || user.role === 'state_notarial_archivist'
  )) {
    return user.portal === 'institute' || user.vertical === 'notarial_regulator';
  }
  return Boolean(user.portal === 'institute');
}

/**
 * Fetch autenticado al namespace /api/institute/...
 * @param {string} path — ruta relativa (con o sin /api), p.ej. "/institute/dashboard"
 * @param {RequestInit & { json?: any }} options
 */
export async function apiFetch(path, options = {}) {
  const { json, headers: optHeaders, ...rest } = options;
  const token = getToken();
  const headers = new Headers(optHeaders || {});

  if (token) headers.set('Authorization', `Bearer ${token}`);

  let body = rest.body;
  if (json !== undefined) {
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  } else if (body && !(body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let url = path || '';
  if (url.startsWith('/api/')) {
    // ya absoluto al proxy
  } else if (url.startsWith('http')) {
    // URL completa
  } else {
    const base = resolveApiBase();
    const clean = url.startsWith('/') ? url : `/${url}`;
    url = `${base}${clean.startsWith('/institute') || clean.startsWith('/notarial') ? clean : `/institute${clean}`}`;
  }

  const response = await fetch(url, { ...rest, headers, body });

  if (response.status === 401) {
    clearSession();
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/instituto/login')) {
      window.location.href = '/instituto/login';
    }
  }

  return response;
}

export async function apiJson(path, options = {}) {
  const res = await apiFetch(path, options);
  let data = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  } else if (res.ok) {
    data = await res.text();
  }
  if (!res.ok) {
    const raw = data?.detail || data?.message || `Error ${res.status}`;
    const detail = formatApiError(raw, `Error ${res.status}`);
    const translated = {
      'Not authenticated': 'Debe iniciar sesión.',
      Forbidden: 'No tiene permiso para esta acción.',
      'Insufficient capabilities': 'No tiene los permisos necesarios.',
      'Feature disabled': 'Esta función no está habilitada.',
      'Institute portal required': 'Se requiere acceso al portal del Instituto.',
      'Invalid credentials': 'Correo o contraseña incorrectos.',
    };
    const err = new Error(translated[detail] || detail);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}
